"use client"

import React, { useState, useMemo } from 'react'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import FormPhoneInput from '@/shared/components/form-inputs/form-phone-input'
import FormOrganizationInput from '@/shared/components/form-inputs/form-organization-input'
import { VolunteerSearchCombobox, VolunteerEmailCombobox, type DefaultServePeriod } from '../../components/volunteer-search-combobox'
import FormSelectInput from '@/shared/components/form-inputs/form-select-input'
import VolunteerFormProfileImage from '../../components/volunteer-form-profile-image'
import FormDateRangeInput from '@/shared/components/form-inputs/form-date-range-input'
import { StateCountryAutocomplete } from '@/shared/components/form-inputs/StateCountryAutocomplete'
import { DialogClose } from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form'
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form'
import { VolunteerFormType } from '../../schemas/volunteers.schema'
import { Plus, X } from 'lucide-react'
import { useRegionOptions } from '@/shared/hooks/use-region-options'
import NoEmailInfoIcon from '@/shared/components/no-email-info-icon'
import { getDisplayEmailValue, isNoEmailPlaceholderValue } from '../../schemas/volunteers.schema'
import { cn } from '@/shared/lib/utils'
import {
    sortServePeriodIndicesChronologically,
    findRelevantServePeriodFieldIndexForHighlight,
} from '../../lib/serve-periods-display'

type VolunteerBaseFormProps = {
    isUpdate?: boolean
    /** When set (e.g. "volunteers.0"), all field names are nested under this path for multi-volunteer create */
    fieldPrefix?: string
    /** Optional label for this entry when in multi-volunteer mode (e.g. "Volunteer 1") */
    entryLabel?: string
    /** When true, do not render Cancel/Submit buttons (used when form is part of multi-volunteer create) */
    hideActions?: boolean
    /** When adding from a group context, append this period to auto-filled serve dates */
    defaultServePeriod?: DefaultServePeriod
    /** Add Volunteer only: group Church/Organization default; omit on Update Volunteer */
    defaultGroupOrganization?: string
}

function VolunteerBaseForm({ isUpdate = false, fieldPrefix, entryLabel, hideActions, defaultServePeriod, defaultGroupOrganization }: VolunteerBaseFormProps) {
    const { formState: { isSubmitting, isDirty }, control, getValues, setValue } = useFormContext<VolunteerFormType | import('../../schemas/volunteers.schema').CreateVolunteersFormType>()
    const [locationDetails, setLocationDetails] = useState<{ city: string; state: string; country: string; latitude?: number; longitude?: number; zipCode?: string } | null>(null)
    const [isEmailEditingOnUpdate, setIsEmailEditingOnUpdate] = useState(false)
    const [emailValueBeforeEdit, setEmailValueBeforeEdit] = useState<string>('')
    const { options: activeRegionOptions, isLoading: regionsLoading } = useRegionOptions(true)
    const isDisabled = isUpdate
        ? (!isDirty || isSubmitting)
        : isSubmitting

    const groupOrgDefault = !isUpdate ? defaultGroupOrganization : undefined

    const name = (field: string) => (fieldPrefix ? `${fieldPrefix}.${field}` : field) as any

    const { fields, append, remove } = useFieldArray({
        control,
        name: name('servePeriods'),
    })

    const watchedServePeriods = useWatch({ control, name: name('servePeriods') })
    const sortedServeFieldIndices = useMemo(
        () =>
            sortServePeriodIndicesChronologically(fields.length, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [fields.length, watchedServePeriods]
    )
    const relevantServeFieldIndex = useMemo(
        () =>
            findRelevantServePeriodFieldIndexForHighlight(sortedServeFieldIndices, (i) => ({
                from: watchedServePeriods?.[i]?.from,
                to: watchedServePeriods?.[i]?.to,
            })),
        [sortedServeFieldIndices, watchedServePeriods]
    )

    const searchOptionalPaths = {
        phone: name('phone'),
        organizationName: name('organizationName'),
        preferredLocation: name('preferredLocation'),
        dateOfBirth: name('dateOfBirth'),
        servePeriods: name('servePeriods'),
        ...(isUpdate ? { profilePicture: name('profilePicture') } : {}),
    }

    return (
        <div className="grid grid-cols-1 gap-x-5 gap-y-3 md:grid-cols-2">
            {entryLabel && (
                <p className="text-sm font-semibold text-foreground md:col-span-2">{entryLabel}</p>
            )}
            {isUpdate && (
                <div className="md:col-span-2">
                    <VolunteerFormProfileImage fieldPrefix={fieldPrefix} />
                </div>
            )}
            <div className="md:col-span-2 space-y-2">
                <p className="text-sm font-medium text-foreground">Required</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 md:items-end">
                    <VolunteerSearchCombobox
                        fullNamePath={name('fullName')}
                        emailPath={name('email')}
                        optionalPaths={searchOptionalPaths}
                        defaultServePeriod={defaultServePeriod}
                        defaultGroupOrganization={groupOrgDefault}
                        inputGroupProps={{
                            label: 'Full Name *',
                            highlightWhenEmpty: true,
                        }}
                        placeholder="Enter or Search by name"
                        disabled={isUpdate}
                        highlightWhenEmpty
                    />
                    {isUpdate ? (
                    <div className="space-y-2">
                        {!isEmailEditingOnUpdate ? (
                            <FormField
                                control={control}
                                name={name('email')}
                                render={({ field, fieldState }) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span>Email *</span>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => {
                                                        const current = String(getValues(name('email')) ?? '')
                                                        setEmailValueBeforeEdit(current)
                                                        setIsEmailEditingOnUpdate(true)
                                                        if (isNoEmailPlaceholderValue(current)) {
                                                            setValue(name('email'), '' as any, {
                                                                shouldDirty: false,
                                                                shouldValidate: true,
                                                            })
                                                        }
                                                    }}
                                                >
                                                    Edit email
                                                </Button>
                                                <NoEmailInfoIcon />
                                            </div>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                value={getDisplayEmailValue(String(field.value ?? ''))}
                                                readOnly
                                                disabled
                                                className="bg-muted"
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        ) : (
                            <FormTextInput
                                name={name('email')}
                                placeholder="Email"
                                type="email"
                                disabled={false}
                                inputGroupProps={{
                                    label: (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span>Email *</span>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 px-2 text-xs"
                                                onClick={() => {
                                                    setValue(name('email'), emailValueBeforeEdit as any, {
                                                        shouldDirty: false,
                                                        shouldValidate: true,
                                                    })
                                                    setIsEmailEditingOnUpdate(false)
                                                }}
                                            >
                                                Cancel email edit
                                            </Button>
                                            <NoEmailInfoIcon />
                                        </div>
                                    ),
                                    highlightWhenEmpty: true,
                                }}
                                control={control}
                            />
                        )}
                    </div>
                ) : (
                    <VolunteerEmailCombobox
                        fullNamePath={name('fullName')}
                        emailPath={name('email')}
                        optionalPaths={searchOptionalPaths}
                        defaultServePeriod={defaultServePeriod}
                        defaultGroupOrganization={groupOrgDefault}
                        inputGroupProps={{
                            label: (
                                <div className="flex items-center gap-1">
                                    <span>Email *</span>
                                    <NoEmailInfoIcon />
                                </div>
                            ),
                            highlightWhenEmpty: true,
                        }}
                        placeholder="Enter or Search by email"
                        disabled={false}
                        highlightWhenEmpty
                    />
                )}
            </div>
            </div>
            <p className="text-sm font-medium text-foreground md:col-span-2">Additional Information</p>
            <FormPhoneInput
                name={name('phone')}
                inputGroupProps={{
                    label: 'Phone Number',
                }}
                placeholder='Phone Number'
                control={control}
            />
            <FormOrganizationInput
                inputGroupProps={{
                    label: 'Organization/Church/Individual',
                }}
                placeholder='Search or type organization name...'
                name={name('organizationName')}
                control={control}
            />
            <div className="md:col-span-2">
                <StateCountryAutocomplete
                    setLocationDetails={setLocationDetails}
                    locationDetails={locationDetails}
                    control={control}
                    id="1"
                    label="Address (including city, state and zip)"
                    fieldName={fieldPrefix ? `${fieldPrefix}.preferredLocation` : undefined}
                />
            </div>
            <div className="md:col-span-2 space-y-4">
                <div className="space-y-3">
                    {sortedServeFieldIndices.map((index, displayPos) => {
                        const field = fields[index]
                        const displayIdx = displayPos + 1
                        const isCurrentPeriod = relevantServeFieldIndex === index
                        return (
                        <div
                            key={field.id}
                            className={cn(
                                "flex items-start gap-3 rounded-lg border p-3 -m-1 transition-colors",
                                isCurrentPeriod
                                    ? "border-emerald-300/90 bg-emerald-50/90 dark:border-emerald-800/60 dark:bg-emerald-950/35"
                                    : "border-transparent"
                            )}
                        >
                            <div className="flex-1 grid grid-cols-1 gap-3 sm:grid-cols-[2fr,1fr]">
                                <FormDateRangeInput
                                    control={control}
                                    inputGroupProps={{
                                        label:
                                            displayIdx === 1
                                                ? 'Serve Arrival and Departure Dates'
                                                : `Serve Period ${displayIdx}`,
                                    }}
                                    name={name(`servePeriods.${index}`)}
                                />
                                <div className="min-w-0">
                                    <FormSelectInput
                                        name={name(`servePeriods.${index}.regionId`)}
                                        inputGroupProps={{ label: 'Region' }}
                                        placeholder={regionsLoading ? "Loading regions..." : "Select region..."}
                                        options={activeRegionOptions}
                                        control={control}
                                        loading={regionsLoading}
                                    />
                                </div>
                            </div>
                            {fields.length > 1 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => remove(index)}
                                    className="mt-8 text-destructive hover:text-destructive"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        )
                    })}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => append({ from: undefined, to: undefined, regionId: undefined })}
                    className="w-full sm:w-auto"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add another serve period
                </Button>
            </div>
            {!hideActions && (
                <div className="grid grid-cols-[120px_1fr] gap-5 pt-2 md:pt-4 md:col-span-2">
                    <DialogClose asChild>
                        <Button
                            type='button'
                            className='w-full'
                            variant={"outline"}
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        className='w-full'
                        disabled={isDisabled}
                        showLoader={isSubmitting}
                    >
                        {isUpdate ? "Update Volunteer" : "Add Volunteer"}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default VolunteerBaseForm